/* eslint-disable max-lines-per-function */
/* eslint-disable indent */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {debounced} from '@mvps-genesis/utilities';
import GTM from 'extensions/gtm';
import 'extensions/ui-components/x-card';
import 'extensions/ui-components/x-product-card';
import {LazyLoadController, SalesBrandsController} from 'extensions/ui-controllers';
import {defineCustomElement} from 'extensions/utils';
import {LitElement, html, nothing} from 'lit';
import {AutocompleteItemType, AutocompleteService, TrackingService} from '../../../services';
import stylesheet from './x-autocomplete-app.component.scss?as=stylesheet';

export class AutocompleteApp extends MobxReactionUpdate(LitElement) {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			autocompleteService: {
				state: true,
				type: Object
			},
			inputSelector: {
				attribute: 'input-selector',
				type: String
			},
			open: {
				type: Boolean
			},
			store: {
				state: true,
				type: Object
			},
			trackingService: {
				state: true,
				type: Object
			}
		};
	}

	get totalResults () {
		return this.store?.state?.totalRecords +
			this.store?.state?.categories?.results?.length +
			this.store?.state?.content?.results?.length +
			this.store?.state?.contentSuggestedQueries?.results?.length +
			this.store?.state?.products?.results?.length +
			this.store?.state?.productSuggestedQueries?.results?.length +
			this.store?.state?.queries?.results?.length;
	}

	constructor () {
		super();

		this.open = true;

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		/**
		 * @type {AutocompleteService}
		 */
		this.autocompleteService = new AutocompleteService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID,
			searchUrl: window.mivaJS.urls.search
		});

		this.autocompleteService.onEvent('afterAutocomplete', this.sendImpressions.bind(this));

		/**
		 * @type {TrackingService}
		 */
		this.trackingService = new TrackingService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID
		});

		/**
		 * @type {import('../../../services').AutocompleteStore}
		 */
		this.store = {};

		/**
		 * @type {string[]}
		 */
		this.saleBrands = [];

		// Create controllers
		this.lazyLoadController = new LazyLoadController(this, this.onLazy);
		this.salesBrandsController = new SalesBrandsController(this);

		// Bind event listeners
		this.handleInput = debounced(250, this.#handleInput.bind(this)).bind(this);
		this.handleFocus = this.#handleFocus.bind(this);
	}

	connectedCallback () {
		super.connectedCallback();

		// Add event listener to input element
		const inputs = document.querySelectorAll(this.inputSelector);
		for (let input of inputs) {
			input.addEventListener('input', this.handleInput);
			input.addEventListener('focus', this.handleFocus);
		}
	}

	disconnectedCallback () {
		// Add event listener to input element
		const inputs = document.querySelectorAll(this.inputSelector);
		for (let input of inputs) {
			input.removeEventListener('input', this.handleInput);
			input.removeEventListener('focus', this.handleFocus);
		}

		super.disconnectedCallback();
	}

	async firstUpdated () {
		// Wait for service store to be fully initialized before setting initial value
		await this.autocompleteService.store.initialized;

		this.store = this.autocompleteService.store;
	}

	updated () {
		const openClass = 'x-autocomplete--open';
		if (!this.open || !this.totalResults) {
			document.body.classList.remove(openClass);
		} else {
			document.body.classList.add(openClass);
		}
	}

	/**
	 * Intersection observer callback
	 */
	async onLazy () {
		// Get sale brands
		this.saleBrands = await this.salesBrandsController.getSalesBrands();
	}

	/**
	 * @param {InputEvent} e
	 */
	#handleInput (e) {
		// Send query to autocomplete service and open the drawer
		this.autocompleteService.query(e.target.value);

		// Set drawer to open
		this.open = true;
	}

	#handleFocus (e) {
		// Set drawer to open
		this.open = true;

		// Run initial query if input element has a value and the store has not been populated yet
		if (!this.store?.state?.query && e.target.value) {
			this.autocompleteService.query(e.target.value);
		}
	}

	handleProductClick (product) {
		// Send request to tracking endpoint
		this.trackingService.trackAutocompleteClick(this.store?.state?.query, AutocompleteItemType.Product, product.title, product.url);
	}

	handleSuggestedQueryClick (suggestedQuery) {
		// Send request to tracking endpoint
		this.trackingService.trackAutocompleteClick(this.store?.state?.query, AutocompleteItemType.Search, suggestedQuery.query, suggestedQuery.url);
	}

	handleSuggestedContentClick (suggestedContent) {
		// Send request to tracking endpoint
		this.trackingService.trackAutocompleteClick(this.store?.state?.query, suggestedContent.id ? AutocompleteItemType.Content : AutocompleteItemType.Category, suggestedContent.title, suggestedContent.url);
	}

	toggleOpen () {
		this.open = !this.open;
	}

	renderProductResults () {
		return this.store?.state?.products?.results?.map(product => {
			return html`
				<x-card>
					<x-product-card
						@click="${e => this.handleProductClick(product, e)}"
						?hidden="${!product.visible}"
						brand="${product?.attributes?.brand}"
						code="${product.id}"
						?discounted-brand="${product?.attributes?.discounted_brands === 'yes'}"
						flag="${product?.attributes?.product_flag}"
						formatted-price="${product.formattedPrice}"
						formatted-retail="${product.formattedRetail}"
						gtm-list="autocomplete"
						?has-metal-options="${product?.attributes?.has_metal_options}"
						hover-image-url="${product?.attributes?.product_image_two_resized || product?.attributes?.product_image_two}"
						image-url="${product?.imageUrl}"
						?incart-pricing="${product?.attributes?.incart_pricing}"
						name="${product.title}"
						price="${product.price}"
						retail="${product.retail}"
						sale-brands="${JSON.stringify(this.saleBrands)}"
						starting-at="${product?.attributes?.starting_at}"
						variant-swatch="${JSON.stringify(product?.attributes?.options_variant_swatch)}"
						url="${product.url}"
					></x-product-card>
				</x-card>
			`;
		});
	}

	renderSuggestedQueries () {
		return [
			...this.store?.state?.productSuggestedQueries?.results ?? [],
			...this.store?.state?.queries?.results ?? [],
			...this.store?.state?.contentSuggestedQueries?.results ?? []
		]
			.slice(0, 4)
			.map(suggestedQuery => {
				return html`<li>
					<a href="${suggestedQuery.url}" @click="${e => this.handleSuggestedQueryClick(suggestedQuery, e)}">${decodeURIComponent(suggestedQuery.query)}</a>
				</li>`;
			});
	}

	renderContent () {
		const contentResults = [
			...this.store?.state?.content?.results ?? [],
			...this.store?.state?.categories?.results ?? []
		].slice(0, 4);

		if (contentResults.length === 0) {
			return nothing;
		}

		return html`
			<div class="content-container">
				<span class="content-header">See:</span>
				<ul class="content-list">${
					contentResults
						.map(suggestedContent => {
							return html`<li>
								<a href="${this.formatSuggestedURL(suggestedContent)}" @click="${e => this.handleSuggestedContentClick(suggestedContent, e)}">${decodeURIComponent(suggestedContent.title)}</a>
							</li>`;
						})
				}</ul>
			</div>
		`;
	}

	renderViewAll () {
		if (this.store?.state?.viewAllUrl) {
			return html`<a class="view-all" href="${this.store.state.viewAllUrl}" title="View All Search Results">View All</a>`;
		}

		return nothing;
	}

	render () {
		return html`<div class="container" ?hidden="${!this.totalResults || !this.open}">
			<div class="inner-wrap">
				<div class="header">
					<ul class="queries-list">${this.renderSuggestedQueries()}</ul>
					<button class="close-button" @click="${this.toggleOpen}" title="Close Search Results" aria-label="Close Search Results">&times;</button>
				</div>
				<div class="products-container" ?hidden="${!this.store?.state?.totalRecords}">
					<div class="products-header">
						<h4 class="products-header-text">${this.store?.state?.products?.title || 'Top Product Matches'}</h4>
						${this.renderViewAll()}
					</div>
					<div class="products-results">${this.renderProductResults()}</div>
				</div>
				${this.renderContent()}
			</div>
		</div>`;
	}

	sendImpressions (data) {
		this.gtm.hawkSearchSendImpressions('autocomplete', 'autocomplete', data?.products?.results);
	}

	formatSuggestedURL (suggestedContent) {
		if (suggestedContent.field !== 'brand') {
			return suggestedContent.url;
		}

		// If brand, set to Search, and remove field from url
		const url = new URL(suggestedContent.url);
		url.searchParams.set('Search', decodeURIComponent(suggestedContent.value));
		url.searchParams.delete(suggestedContent.field);

		return url.toString();
	}
};

defineCustomElement('x-autocomplete-app', AutocompleteApp);

